import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Archive,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldAlert,
  Tag,
  X,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Badge, Button, Card } from '@/components/ui'
import { cn } from '@/lib/utils'
import {
  fetchCrmSourceAccountDetail,
  fetchCrmSourceAccounts,
  setCrmAccountKind,
  setRevenueSourceStatus,
  type CrmAccountKind,
  type CrmSourceAccountDetail,
  type CrmSourceAccountRow,
  type RevenueSourceStatus,
} from '@/services/crmSourceAccountService'
import {
  fetchRevenueSyncIncidents,
  isRevenueSyncIncidentResolutionRpcMissing,
  resolveRevenueSyncIncidentWithSourceStatus,
  type RevenueSyncIncidentRow,
} from '@/services/revenueSyncIncidentService'

const STATUS_LABELS: Record<RevenueSourceStatus, string> = {
  ACTIVE: 'Активен',
  QUARANTINED: 'Карантин',
  RETIRED: 'Закрыт навсегда',
}

const STATUS_TONE: Record<RevenueSourceStatus, string> = {
  ACTIVE: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  QUARANTINED: 'border-amber-200 bg-amber-50 text-amber-700',
  RETIRED: 'border-zinc-200 bg-zinc-50 text-zinc-600',
}

const KIND_LABELS: Record<CrmAccountKind, string> = {
  FRANCHISEE: 'Франчайзи',
  SERVICE: 'Служебный',
}

const OBSERVED_CLASS_LABELS: Record<string, string> = {
  franchisee: 'есть номерные студии',
  no_numbered_studios: 'нет номерных студий',
  unknown: 'нет платежей',
}

type AccountFilter = 'attention' | 'quarantined' | 'retired' | 'all'

const ACCOUNT_FILTERS: Array<{ value: AccountFilter; label: string }> = [
  { value: 'attention', label: 'Требуют действия' },
  { value: 'quarantined', label: 'Карантин' },
  { value: 'retired', label: 'Закрыты навсегда' },
  { value: 'all', label: 'Все' },
]

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatAmount(value: number, currency: string): string {
  const amount = Number(value ?? 0)
  try {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${currency}`
  }
}

function formatAmounts(amounts: Record<string, number> | null | undefined): string {
  const entries = Object.entries(amounts ?? {})
  if (entries.length === 0) return '—'
  return entries.map(([currency, amount]) => formatAmount(amount, currency)).join(' · ')
}

function daysSince(value: string | null | undefined): number | null {
  if (!value) return null
  const then = new Date(value).getTime()
  if (!Number.isFinite(then)) return null
  return Math.max(0, Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000)))
}

function studioList(numbers: number[] | null | undefined): string {
  if (!numbers || numbers.length === 0) return '—'
  return numbers.map(value => `№${value}`).join(', ')
}

function incidentStudioNumber(incident: RevenueSyncIncidentRow): number | null {
  if (!incident.studio_number) return null
  const parsed = Number(incident.studio_number)
  return Number.isFinite(parsed) ? parsed : null
}

function incidentTitleText(incident: RevenueSyncIncidentRow): string {
  if (incident.last_message) return incident.last_message

  const studio = incidentStudioNumber(incident)
  const scope = studio == null ? 'аккаунт' : `студия №${studio}`
  const labels: Record<string, string> = {
    account_absent_from_run: 'Аккаунт целиком не пришёл в выгрузку',
    account_absent_from_staging: 'Аккаунт целиком не пришёл в выгрузку',
    studio_absent_from_run: 'Пара аккаунт + студия не пришла в выгрузку',
    source_returned: 'Источник вернулся в выгрузку — проверьте полноту',
    quarantine_review_overdue: 'Пересмотр карантина просрочен',
  }

  return `${labels[incident.incident_type] ?? incident.incident_type}: ${scope}`
}

function incidentMatchesRow(
  incident: RevenueSyncIncidentRow,
  row: CrmSourceAccountRow
): boolean {
  if (!incident.account || incident.account.toLowerCase() !== row.account.toLowerCase()) {
    return false
  }

  const studio = incidentStudioNumber(incident)
  if (studio == null) return true

  return (
    row.current_studio_numbers.includes(studio) ||
    row.historical_studio_numbers.includes(studio) ||
    row.missing_studio_numbers.includes(studio) ||
    row.exception_studio_number === studio
  )
}

/**
 * Единый формат подписи из плана: `Город — account — № студии`.
 * Города может не быть в справочнике — тогда так и пишем, а не молчим.
 */
export function accountTitle(row: CrmSourceAccountRow): string {
  const city = row.city_name?.trim() || 'город не указан'
  const studios = row.current_studio_numbers.length > 0
    ? ` — ${studioList(row.current_studio_numbers)}`
    : ''
  return `${city} — ${row.account}${studios}`
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

/** Что именно требует действия по этой строке. Пусто = ничего не требует. */
function attentionReasons(
  row: CrmSourceAccountRow,
  incidents: RevenueSyncIncidentRow[] = []
): string[] {
  const reasons: string[] = []
  const incidentTypes = new Set(incidents.map(incident => incident.incident_type))

  for (const incident of incidents) {
    reasons.push(incidentTitleText(incident))
  }

  if (row.source_return_candidate && !incidentTypes.has('source_returned')) {
    reasons.push('Источник вернулся в выгрузку — проверьте полноту и снимите карантин')
  }

  const hasSourceLossIncident =
    incidentTypes.has('account_absent_from_run') ||
    incidentTypes.has('account_absent_from_staging') ||
    incidentTypes.has('studio_absent_from_run')
  if (row.missing_kind && row.source_status === 'ACTIVE' && !hasSourceLossIncident) {
    reasons.push(
      row.missing_kind === 'account'
        ? 'Аккаунт целиком не пришёл в выгрузку'
        : `Не пришла пара аккаунт + студия ${studioList(row.missing_studio_numbers)}`
    )
  }

  if (row.exception_review_overdue && !incidentTypes.has('quarantine_review_overdue')) {
    reasons.push(`Пересмотр карантина просрочен с ${formatDate(row.exception_review_by)}`)
  }

  return reasons
}

function Chip({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <span className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>
      {children}
    </span>
  )
}

function PanelButton({
  children,
  icon,
  tone = 'secondary',
  className,
  ...props
}: {
  children: ReactNode
  icon?: ReactNode
  tone?: 'primary' | 'secondary' | 'danger'
  className?: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:pointer-events-none disabled:opacity-50',
        tone === 'primary' && 'bg-primary text-primary-foreground hover:bg-primary/90',
        tone === 'secondary' && 'border border-border bg-background text-foreground hover:bg-muted/60',
        tone === 'danger' && 'border border-red-300 bg-red-50 text-red-700 hover:bg-red-100',
        className
      )}
      {...props}
    >
      {icon}
      <span>{children}</span>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Модалка решения по источнику
//
// Не window.prompt: у решения три поля с разными правилами (причина ≥ 10
// символов, дата пересмотра обязательна ровно у карантина и запрещена у
// постоянного закрытия). Сервер это проверяет сам, но оператор должен видеть
// правило до отправки, а не ловить исключение из RPC.
// ---------------------------------------------------------------------------

type StatusDraft = {
  row: CrmSourceAccountRow
  status: RevenueSourceStatus
  incidentKey: string | null
}

const STATUS_ACTION_TITLES: Record<RevenueSourceStatus, string> = {
  QUARANTINED: 'Поместить источник в карантин',
  RETIRED: 'Закрыть источник навсегда',
  ACTIVE: 'Вернуть источник в работу',
}

function defaultReviewDate(): string {
  const date = new Date()
  date.setDate(date.getDate() + 30)
  return date.toISOString().slice(0, 10)
}

function StatusDecisionModal({
  draft,
  busy,
  onCancel,
  onSubmit,
}: {
  draft: StatusDraft
  busy: boolean
  onCancel: () => void
  onSubmit: (params: {
    reason: string
    reviewBy: string | null
    expectedReturn: string | null
    note: string | null
    studioNumber: number | null
  }) => void
}) {
  const { row, status } = draft
  const [reason, setReason] = useState('')
  const [reviewBy, setReviewBy] = useState(status === 'QUARANTINED' ? defaultReviewDate() : '')
  const [expectedReturn, setExpectedReturn] = useState('')
  const [note, setNote] = useState('')

  // Снятие исключения обязано попасть в ТОТ ЖЕ скоуп, что и действующее:
  // иначе «снять карантин» с узкой пары ушло бы в несуществующее аккаунтное
  // исключение и упало бы на «источник уже в статусе ACTIVE».
  const scopeLocked = row.source_status !== 'ACTIVE'
  const suggestedStudio = row.missing_kind === 'account_studio'
    ? row.missing_studio_numbers[0] ?? null
    : null
  const [studioNumber, setStudioNumber] = useState<number | null>(
    scopeLocked ? row.exception_studio_number : suggestedStudio
  )
  const scopeOptions = useMemo(() => {
    const numbers = new Set<number>(row.current_studio_numbers)
    row.missing_studio_numbers.forEach(value => numbers.add(value))
    if (row.exception_studio_number !== null) numbers.add(row.exception_studio_number)
    return [...numbers].sort((a, b) => a - b)
  }, [row])

  const reasonTooShort = reason.trim().length < 10
  const reviewMissing = status === 'QUARANTINED' && !reviewBy
  const canSubmit = !reasonTooShort && !reviewMissing && !busy

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <Card className="mt-10 w-full max-w-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold">{STATUS_ACTION_TITLES[status]}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{accountTitle(row)}</div>
          </div>
          <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 rounded border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          {status === 'QUARANTINED' && (
            <>
              Карантин снимает блокировку прогона для всей сети, но <b>не</b> защиту данных:
              строки аккаунта остаются живыми, выручка студии замирает на последней полученной
              дате. Дата пересмотра обязательна — без неё «временно» превращается в бессрочную
              слепоту.
            </>
          )}
          {status === 'RETIRED' && (
            <>
              Постоянно закрытый источник перестаёт ожидаться в выгрузке и не повторяется в
              ежедневной сводке. Данные остаются активными и защищёнными от удаления. Даты
              пересмотра у такого решения нет.
            </>
          )}
          {status === 'ACTIVE' && (
            <>
              Источник снова ожидается в выгрузке. Перед возвратом убедитесь, что данные пришли
              полностью: замершие дни пайплайн не досчитает задним числом сам.
            </>
          )}
        </div>

        {/* Скоуп исключения. Карантин на весь аккаунт снимает блокировку
            preflight по ВСЕМ его студиям, поэтому при пропаже одной пары его
            не предлагаем по умолчанию. У действующего исключения скоуп менять
            нечем: снятие обязано попасть в ту же строку реестра. */}
        {scopeOptions.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-medium text-muted-foreground">Область решения</div>
            <select
              value={studioNumber === null ? '' : String(studioNumber)}
              onChange={event =>
                setStudioNumber(event.target.value === '' ? null : Number(event.target.value))
              }
              disabled={scopeLocked}
              className={cn(
                'mt-1 w-full rounded border border-border px-2 py-2 text-sm text-foreground outline-none focus:border-primary',
                scopeLocked ? 'bg-muted/30' : 'bg-background'
              )}
            >
              <option value="">Весь аккаунт {row.account}</option>
              {scopeOptions.map(number => (
                <option key={number} value={number}>
                  Только студия №{number}
                </option>
              ))}
            </select>
            <div className="mt-1 text-[11px] text-muted-foreground">
              {scopeLocked
                ? 'Область берётся из действующего исключения и не меняется: решение снимает именно его.'
                : studioNumber === null
                  ? 'Исключение накроет все студии аккаунта.'
                  : `Остальные студии аккаунта продолжат защищаться preflight как обычно.`}
            </div>
          </div>
        )}

        <label className="mt-3 block text-xs font-medium text-muted-foreground">
          Причина решения (обязательно, не короче 10 символов)
          <textarea
            value={reason}
            onChange={event => setReason(event.target.value)}
            rows={3}
            autoFocus
            placeholder={
              status === 'RETIRED'
                ? 'например: аккаунт заблокирован в Листке навсегда, франчайзи вышел из сети'
                : 'например: аккаунт заблокирован за неоплату, ждём оплаты франчайзи'
            }
            className="mt-1 w-full resize-none rounded border border-border bg-background px-2 py-2 text-sm font-normal text-foreground outline-none focus:border-primary"
          />
          {reasonTooShort && reason.length > 0 && (
            <span className="mt-1 block text-[11px] font-normal text-red-600">
              Ещё {10 - reason.trim().length} символов
            </span>
          )}
        </label>

        {status === 'QUARANTINED' && (
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
            <label className="text-xs font-medium text-muted-foreground">
              Дата пересмотра
              <input
                type="date"
                value={reviewBy}
                onChange={event => setReviewBy(event.target.value)}
                className="mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary"
              />
            </label>
            <label className="text-xs font-medium text-muted-foreground">
              Что ждём для возврата
              <input
                value={expectedReturn}
                onChange={event => setExpectedReturn(event.target.value)}
                placeholder="например: оплата франчайзи"
                className="mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm font-normal text-foreground outline-none focus:border-primary"
              />
            </label>
          </div>
        )}

        <label className="mt-2 block text-xs font-medium text-muted-foreground">
          Комментарий (необязательно)
          <input
            value={note}
            onChange={event => setNote(event.target.value)}
            className="mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm font-normal text-foreground outline-none focus:border-primary"
          />
        </label>

        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" size="sm" variant="outline" onClick={onCancel} disabled={busy}>
            Отмена
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                reason: reason.trim(),
                reviewBy: status === 'QUARANTINED' ? reviewBy : null,
                expectedReturn: expectedReturn.trim() || null,
                note: note.trim() || null,
                studioNumber,
              })
            }
          >
            {busy ? <RefreshCw className="mr-1 h-4 w-4 animate-spin" /> : null}
            Применить
          </Button>
        </div>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Раскрытая карточка аккаунта
// ---------------------------------------------------------------------------

function AccountDetail({ account }: { account: string }) {
  const [detail, setDetail] = useState<CrmSourceAccountDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchCrmSourceAccountDetail(account)
      .then(result => {
        if (!cancelled) setDetail(result)
      })
      .catch((loadError: unknown) => {
        if (!cancelled) setError(errorMessage(loadError, 'Не удалось загрузить карточку аккаунта'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [account])

  if (loading) {
    return <div className="mt-3 text-xs text-muted-foreground">Загружаем карточку аккаунта…</div>
  }

  if (error) {
    return <div className="mt-3 text-xs text-red-600">{error}</div>
  }

  if (!detail) return null

  return (
    <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
      <div className="rounded-lg border border-border bg-muted/20 p-3">
        <div className="text-xs font-semibold">Привязки студий по датам</div>
        {detail.studioLinks.length === 0 ? (
          <div className="mt-1 text-xs text-muted-foreground">Привязок нет.</div>
        ) : (
          <div className="mt-2 space-y-1">
            {detail.studioLinks.map(link => (
              <div key={link.link_id} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                <span className="font-medium">№{link.studio_number ?? '—'}</span>
                <span className="text-muted-foreground">{link.city_name ?? 'город не указан'}</span>
                <span className="text-muted-foreground">
                  {formatDate(link.valid_from)} — {link.valid_to ? formatDate(link.valid_to) : 'сейчас'}
                </span>
                {link.is_current && <Chip tone="border-emerald-200 bg-emerald-50 text-emerald-700">текущая</Chip>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-muted/20 p-3">
        <div className="text-xs font-semibold">Владелец аккаунта по датам</div>
        {detail.partnerLinks.length === 0 ? (
          <div className="mt-1 text-xs text-muted-foreground">Привязки к франчайзи нет.</div>
        ) : (
          <div className="mt-2 space-y-1">
            {detail.partnerLinks.map(link => (
              <div key={`${link.partner_number}-${link.valid_from}`} className="text-xs">
                <span className="font-medium">
                  {link.persons_fio || link.owner_fio_raw || `Франчайзи ${link.partner_number ?? '—'}`}
                </span>
                <span className="ml-2 text-muted-foreground">
                  {formatDate(link.valid_from)} — {link.valid_to ? formatDate(link.valid_to) : 'сейчас'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-muted/20 p-3">
        <div className="text-xs font-semibold">Кейсы переноса</div>
        {detail.transferCases.length === 0 ? (
          <div className="mt-1 text-xs text-muted-foreground">Кейсов нет.</div>
        ) : (
          <div className="mt-2 space-y-1">
            {detail.transferCases.map(item => (
              <div key={item.case_id} className="text-xs">
                <span className="font-medium">#{item.case_id}</span>{' '}
                <span className="text-muted-foreground">
                  {item.role === 'old' ? 'отдаёт' : 'принимает'} №{item.studio_number ?? '—'} ·{' '}
                  {item.old_account ?? '—'} → {item.proposed_account ?? '—'} ·{' '}
                  {formatDate(item.transition_date)} · {item.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-muted/20 p-3">
        <div className="text-xs font-semibold">История решений по источнику</div>
        {detail.decisions.length === 0 ? (
          <div className="mt-1 text-xs text-muted-foreground">Решений не принималось.</div>
        ) : (
          <div className="mt-2 space-y-2">
            {detail.decisions.map(decision => (
              <div key={decision.exception_id} className="text-xs">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Chip tone={STATUS_TONE[decision.status]}>{STATUS_LABELS[decision.status]}</Chip>
                  {decision.studio_number !== null && (
                    <span className="text-muted-foreground">№{decision.studio_number}</span>
                  )}
                  {decision.is_active ? (
                    <Chip tone="border-sky-200 bg-sky-50 text-sky-700">действует</Chip>
                  ) : (
                    <span className="text-muted-foreground">
                      снято {formatDateTime(decision.released_at)} · {decision.release_kind}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-muted-foreground">{decision.reason}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {formatDateTime(decision.status_changed_at ?? decision.quarantined_at)}
                  {decision.status_changed_by ? ` · ${decision.status_changed_by}` : ''}
                  {decision.review_by ? ` · пересмотр к ${formatDate(decision.review_by)}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Строка аккаунта
// ---------------------------------------------------------------------------

function AccountRow({
  row,
  incidents,
  expanded,
  busy,
  onToggle,
  onStatus,
  onKind,
}: {
  row: CrmSourceAccountRow
  incidents: RevenueSyncIncidentRow[]
  expanded: boolean
  busy: boolean
  onToggle: () => void
  onStatus: (status: RevenueSourceStatus, incidentKey?: string | null) => void
  onKind: (kind: CrmAccountKind | null) => void
}) {
  const navigate = useNavigate()
  const reasons = attentionReasons(row, incidents)
  const silentDays = daysSince(row.last_paid_on ? `${row.last_paid_on}T00:00:00` : null)
  const primaryIncident = incidents[0] ?? null

  return (
    <div className="border-b border-border px-3 py-3 last:border-b-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onToggle}
              className="inline-flex items-center gap-1 text-sm font-semibold text-foreground"
            >
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {accountTitle(row)}
            </button>
            <Chip tone={STATUS_TONE[row.source_status]}>{STATUS_LABELS[row.source_status]}</Chip>
            {row.account_kind ? (
              <Chip tone="border-sky-200 bg-sky-50 text-sky-700">{KIND_LABELS[row.account_kind]}</Chip>
            ) : (
              <Chip tone="border-border bg-muted/40 text-muted-foreground">
                тип не задан · {OBSERVED_CLASS_LABELS[row.observed_class] ?? row.observed_class}
              </Chip>
            )}
            {row.source_return_candidate && (
              <Chip tone="border-sky-300 bg-sky-50 text-sky-700">источник вернулся</Chip>
            )}
            {primaryIncident && (
              <Chip tone="border-amber-200 bg-amber-50 text-amber-800">
                incident #{primaryIncident.incident_id}
              </Chip>
            )}
            {row.missing_kind && (
              <Chip tone="border-red-200 bg-red-50 text-red-700">
                {row.missing_kind === 'account' ? 'нет в выгрузке' : 'нет пары со студией'}
              </Chip>
            )}
            {row.active_exception_count > 1 && (
              <Chip tone="border-border bg-muted/40 text-muted-foreground">
                исключений: {row.active_exception_count}
              </Chip>
            )}
          </div>

          <div className="mt-1 text-xs text-muted-foreground">
            {row.account_fio || 'ФИО не указано'}
            {row.partner_numbers.length > 0 && ` · франчайзи ${row.partner_numbers.join(', ')}`}
          </div>

          {reasons.length > 0 && (
            <div className="mt-2 space-y-1">
              {reasons.map(reason => (
                <div
                  key={reason}
                  className="flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800"
                >
                  <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{reason}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs md:grid-cols-4">
            <div>
              <div className="text-muted-foreground">Последний платёж</div>
              <div className="font-medium">
                {formatDate(row.last_paid_on)}
                {silentDays !== null && silentDays > 0 && (
                  <span className="ml-1 font-normal text-muted-foreground">({silentDays} дн. назад)</span>
                )}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Живых строк</div>
              <div className="font-medium">
                {row.live_payment_count.toLocaleString('ru-RU')}
                {row.blank_payment_count > 0 && (
                  <span className="ml-1 font-normal text-amber-700">
                    · без филиала {row.blank_payment_count}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Заморожено</div>
              <div className="font-medium">
                {row.frozen_row_count > 0
                  ? `${row.frozen_row_count} стр. · ${formatAmounts(row.frozen_amounts)}`
                  : '—'}
              </div>
            </div>
            <div>
              {/* ⚠️ Именно «изменение строк», а не «выгрузка»: merge бампает
                  updated_at только при реальном изменении, поэтому исправный
                  аккаунт без новых продаж выглядит молчащим. */}
              <div className="text-muted-foreground">Строки менялись</div>
              <div className="font-medium">{formatDateTime(row.last_item_change_at)}</div>
            </div>
          </div>

          {row.missing_kind && (
            <div className="mt-2 text-xs text-muted-foreground">
              Вердикт прогона {row.missing_run_mode} от {formatDateTime(row.missing_detected_at)}:
              защищено {row.missing_baseline_rows} строк
              {Number(row.missing_abs_amount) > 0 &&
                ` на ${Number(row.missing_abs_amount).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}`}
              . Данные оставлены живыми, удаления подавлены.
            </div>
          )}

          {row.source_status !== 'ACTIVE' && row.exception_reason && (
            <div className="mt-2 rounded border border-border bg-muted/30 px-2 py-1.5 text-xs">
              <div className="text-foreground">{row.exception_reason}</div>
              <div className="mt-0.5 text-muted-foreground">
                {formatDateTime(row.exception_changed_at)}
                {row.exception_changed_by ? ` · ${row.exception_changed_by}` : ''}
                {row.source_status === 'QUARANTINED' && row.exception_review_by
                  ? ` · пересмотр к ${formatDate(row.exception_review_by)}`
                  : ''}
                {row.exception_scope === 'account_studio' && row.exception_studio_number !== null
                  ? ` · только студия №${row.exception_studio_number}`
                  : ''}
              </div>
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-start gap-1.5">
          {row.source_status === 'ACTIVE' && (
            <>
              <PanelButton
                onClick={() => onStatus('QUARANTINED')}
                disabled={busy}
                icon={<AlertTriangle className="h-3.5 w-3.5" />}
                title="Источник временно не ожидается: preflight не блокирует сеть, данные замирают"
              >
                Карантин
              </PanelButton>
              <PanelButton
                tone="danger"
                onClick={() => onStatus('RETIRED')}
                disabled={busy}
                icon={<Archive className="h-3.5 w-3.5" />}
                title="Источник закрыт навсегда: новые данные не ожидаются, даты пересмотра нет"
              >
                Закрыть навсегда
              </PanelButton>
            </>
          )}

          {row.source_status === 'QUARANTINED' && (
            <>
              <PanelButton
                tone="primary"
                onClick={() => onStatus('ACTIVE')}
                disabled={busy}
                icon={<CheckCircle2 className="h-3.5 w-3.5" />}
              >
                Снять карантин
              </PanelButton>
              <PanelButton
                tone="danger"
                onClick={() => onStatus('RETIRED')}
                disabled={busy}
                icon={<Archive className="h-3.5 w-3.5" />}
              >
                Закрыть навсегда
              </PanelButton>
            </>
          )}

          {row.source_status === 'RETIRED' && (
            <PanelButton
              tone="primary"
              onClick={() => onStatus('ACTIVE')}
              disabled={busy}
              icon={<RotateCcw className="h-3.5 w-3.5" />}
            >
              Вернуть в работу
            </PanelButton>
          )}

          <PanelButton
            onClick={() => onKind(row.account_kind === 'SERVICE' ? null : 'SERVICE')}
            disabled={busy}
            icon={row.account_kind === 'SERVICE' ? <Ban className="h-3.5 w-3.5" /> : <Tag className="h-3.5 w-3.5" />}
            title="Тип аккаунта — ось, независимая от статуса: служебный аккаунт может быть активным"
          >
            {row.account_kind === 'SERVICE' ? 'Снять служебный' : 'Отметить служебным'}
          </PanelButton>

          <PanelButton
            onClick={() =>
              navigate(`/management/missing-payments?account=${encodeURIComponent(row.account)}`)
            }
            icon={<ExternalLink className="h-3.5 w-3.5" />}
            title="Платёжные доказательства по этому аккаунту"
          >
            Платежи
          </PanelButton>
        </div>
      </div>

      {expanded && <AccountDetail account={row.account} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Панель
// ---------------------------------------------------------------------------

export function CrmSourceAccountsPanel({
  deepLinkAccount,
  deepLinkIncident,
  onDeepLinkConsumed,
}: {
  deepLinkAccount?: string | null
  deepLinkIncident?: string | null
  onDeepLinkConsumed?: () => void
}) {
  const [rows, setRows] = useState<CrmSourceAccountRow[]>([])
  const [openIncidents, setOpenIncidents] = useState<RevenueSyncIncidentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<AccountFilter>('attention')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [draft, setDraft] = useState<StatusDraft | null>(null)
  const [busyAccount, setBusyAccount] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [deepLinkIncidentKey, setDeepLinkIncidentKey] = useState<string | null>(
    () => deepLinkIncident?.trim() || null
  )

  async function load() {
    setLoading(true)
    setError(null)
    try {
      setRows(await fetchCrmSourceAccounts())
      setOpenIncidents(
        await fetchRevenueSyncIncidents({ status: 'OPEN', limit: 500 }).catch(
          () => [] as RevenueSyncIncidentRow[]
        )
      )
    } catch (loadError: unknown) {
      setError(errorMessage(loadError, 'Не удалось загрузить список CRM-аккаунтов'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  // Deep-link `?account=an7221`: подставляем в поиск, снимаем фильтр (аккаунт
  // может быть штатным) и сразу раскрываем карточку.
  useEffect(() => {
    const account = deepLinkAccount?.trim()
    if (!account) return
    setSearch(account)
    setFilter('all')
    setExpanded(account)
    setDeepLinkIncidentKey(deepLinkIncident?.trim() || null)
    onDeepLinkConsumed?.()
  }, [deepLinkAccount, deepLinkIncident, onDeepLinkConsumed])

  const stats = useMemo(() => {
    const attention = rows.filter(row => attentionReasons(row, incidentsForRow(row)).length > 0).length
    const quarantined = rows.filter(row => row.source_status === 'QUARANTINED').length
    const retired = rows.filter(row => row.source_status === 'RETIRED').length
    return { total: rows.length, attention, quarantined, retired }
  }, [rows, openIncidents])

  function incidentsForRow(row: CrmSourceAccountRow): RevenueSyncIncidentRow[] {
    return openIncidents.filter(incident => incidentMatchesRow(incident, row))
  }

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase().replace(/ё/g, 'е')
    return rows
      .filter(row => {
        const incidents = incidentsForRow(row)
        if (filter === 'attention') return attentionReasons(row, incidents).length > 0
        if (filter === 'quarantined') return row.source_status === 'QUARANTINED'
        if (filter === 'retired') return row.source_status === 'RETIRED'
        return true
      })
      .filter(row => {
        if (!query) return true
        const haystack = [
          row.account,
          row.city_name,
          row.country_name,
          row.account_fio,
          ...row.current_studio_numbers,
          ...row.historical_studio_numbers,
          ...row.partner_numbers,
        ]
          .filter(value => value !== null && value !== undefined)
          .join(' ')
          .toLowerCase()
          .replace(/ё/g, 'е')
        return haystack.includes(query)
      })
      .sort((a, b) => {
        const byAttention =
          attentionReasons(b, incidentsForRow(b)).length -
          attentionReasons(a, incidentsForRow(a)).length
        if (byAttention !== 0) return byAttention
        return a.account.localeCompare(b.account)
      })
  }, [rows, filter, search, openIncidents])

  async function applyStatus(params: {
    reason: string
    reviewBy: string | null
    expectedReturn: string | null
    note: string | null
    studioNumber: number | null
  }) {
    if (!draft) return
    const { row, status, incidentKey } = draft
    setBusyAccount(row.account)
    setError(null)
    setSuccess(null)
    try {
      let resolvedInOneOperation = false
      let result: Awaited<ReturnType<typeof setRevenueSourceStatus>> | null = null
      let resolvedResult: Awaited<ReturnType<typeof resolveRevenueSyncIncidentWithSourceStatus>> | null = null
      if (incidentKey) {
        try {
          resolvedResult = await resolveRevenueSyncIncidentWithSourceStatus({
            incidentKey,
            sourceStatus: status,
            reason: params.reason,
            studioNumber: params.studioNumber,
            reviewBy: params.reviewBy,
            expectedReturn: params.expectedReturn,
            note: params.note,
          })
          resolvedInOneOperation = true
        } catch (resolutionError) {
          // The lifecycle write remains available while PROD's migration ledger
          // is recovered. Only an absent additive RPC may fall back: validation
          // and permission errors must stay visible to the operator.
          if (!isRevenueSyncIncidentResolutionRpcMissing(resolutionError)) throw resolutionError
        }
      }
      if (!resolvedResult) {
        result = await setRevenueSourceStatus({
          account: row.account,
          status,
          reason: params.reason,
          // Скоуп приходит из модалки: у действующего исключения он зафиксирован
          // (снятие обязано попасть в ту же строку реестра), у нового — выбирается,
          // чтобы пропажа одной пары не снимала защиту со всех студий аккаунта.
          studioNumber: params.studioNumber,
          reviewBy: params.reviewBy,
          expectedReturn: params.expectedReturn,
          note: params.note,
          incidentRef: incidentKey,
        })
      }
      const scopeStudioNumber = resolvedResult
        ? resolvedResult.studio_number
        : result!.studio_number
      const scopeLabel = scopeStudioNumber === null
        ? 'весь аккаунт'
        : `студия №${scopeStudioNumber}`
      setSuccess(
        resolvedInOneOperation
          ? `${row.account} (${scopeLabel}): источник переведён в «${STATUS_LABELS[status]}», incident закрыт.`
          : `${row.account} (${scopeLabel}): ${STATUS_LABELS[result.previous_status]} → ${STATUS_LABELS[result.status]}`
      )
      setDraft(null)
      await load()
    } catch (actionError: unknown) {
      setError(errorMessage(actionError, 'Не удалось изменить статус источника'))
    } finally {
      setBusyAccount(null)
    }
  }

  async function applyKind(row: CrmSourceAccountRow, kind: CrmAccountKind | null) {
    const confirmed = window.confirm(
      kind === 'SERVICE'
        ? `Отметить ${row.account} служебным аккаунтом?\n\nСлужебная выручка без филиала перестанет считаться ошибкой франчайзи. Статус источника при этом не меняется.`
        : `Снять со счёта ${row.account} отметку «служебный»?`
    )
    if (!confirmed) return

    setBusyAccount(row.account)
    setError(null)
    setSuccess(null)
    try {
      await setCrmAccountKind({ account: row.account, kind })
      setSuccess(
        kind === 'SERVICE'
          ? `${row.account} отмечен служебным`
          : `С ${row.account} снята отметка «служебный»`
      )
      await load()
    } catch (actionError: unknown) {
      setError(errorMessage(actionError, 'Не удалось изменить тип аккаунта'))
    } finally {
      setBusyAccount(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Всего аккаунтов</div>
          <div className="text-xl font-semibold">{stats.total}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Требуют действия</div>
          <div className={cn('text-xl font-semibold', stats.attention > 0 && 'text-amber-700')}>
            {stats.attention}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">В карантине</div>
          <div className="text-xl font-semibold">{stats.quarantined}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Закрыты навсегда</div>
          <div className="text-xl font-semibold">{stats.retired}</div>
        </Card>
      </div>

      <Card className="p-3">
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Аккаунт, город, ФИО, номер студии или франчайзи"
              className="w-full rounded border border-border bg-background py-2 pl-8 pr-3 text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="inline-flex shrink-0 flex-wrap rounded-md border border-border bg-muted/30 p-0.5">
            {ACCOUNT_FILTERS.map(item => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFilter(item.value)}
                className={cn(
                  'h-7 whitespace-nowrap rounded px-2.5 text-xs font-medium transition-colors',
                  filter === item.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
          <PanelButton
            onClick={() => void load()}
            disabled={loading}
            icon={<RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />}
          >
            Обновить
          </PanelButton>
        </div>
      </Card>

      {error && (
        <Card className="border-red-500/40 bg-red-500/5 p-3">
          <div className="flex items-start gap-2 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {success && (
        <Card className="border-emerald-500/40 bg-emerald-500/5 p-3">
          <div className="flex items-start gap-2 text-sm text-emerald-700">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{success}</span>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div className="text-sm font-semibold">Источники выручки</div>
          <Badge>{visibleRows.length}</Badge>
        </div>
        {loading ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">Загрузка…</div>
        ) : visibleRows.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">
            {filter === 'attention'
              ? 'Ни один источник не требует действия.'
              : 'Ничего не найдено.'}
          </div>
        ) : (
          visibleRows.map(row => (
            <AccountRow
              key={row.account}
              row={row}
              incidents={incidentsForRow(row)}
              expanded={expanded === row.account}
              busy={busyAccount === row.account}
              onToggle={() => setExpanded(expanded === row.account ? null : row.account)}
              onStatus={(status, incidentKey = incidentsForRow(row)[0]?.incident_key ?? null) =>
                setDraft({
                  row,
                  status,
                  incidentKey:
                    deepLinkIncidentKey && row.account.toLowerCase() === search.trim().toLowerCase()
                      ? deepLinkIncidentKey
                      : incidentKey,
                })
              }
              onKind={kind => void applyKind(row, kind)}
            />
          ))
        )}
      </Card>

      {draft && (
        <StatusDecisionModal
          draft={draft}
          busy={busyAccount === draft.row.account}
          onCancel={() => setDraft(null)}
          onSubmit={params => void applyStatus(params)}
        />
      )}
    </div>
  )
}
